import { useState } from "react";
import axios from "axios";
import {
  CheckCircle2,
  ClipboardList,
  Copy,
  Link as LinkIcon,
  Send,
  CalendarPlus,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API } from "@/context/AuthContext";

const WEEKDAY_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function formatSlotLabel(iso) {
  try {
    const d = new Date(iso);
    const wd = WEEKDAY_PT[d.getDay()];
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${wd} ${dd}/${mm} · ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

/**
 * InterviewSuccessDialog
 *
 * Shows the result of a successful interview scheduling: host + candidate
 * join links, copy buttons, and a "Send via WhatsApp" button that sends
 * through Synkly and registers in message history.
 *
 * Props:
 *   open: boolean
 *   onClose(): void
 *   interview: { id, starts_at, ... }
 *   room: { host_join_url, candidate_join_url } | null
 *   candidateId: string
 *   candidateName: string
 *   candidatePhone: string
 *   jobTitle: string
 *   onCompleteInfo(): optional — when provided, shows a button to open the
 *     "complete candidate data" form (address, documents, references, etc.)
 */
const InterviewSuccessDialog = ({
  open,
  onClose,
  interview,
  room,
  candidateId = "",
  candidateName = "",
  candidatePhone = "",
  jobTitle = "",
  onCompleteInfo,
}) => {
  const [sendingWa, setSendingWa] = useState(false);

  if (!interview) return null;

  const candidateLink = room?.candidate_join_url || "";
  const hostLink = room?.host_join_url || "";
  const startsAtLabel = interview.starts_at
    ? formatSlotLabel(interview.starts_at)
    : "";

  const waMessage =
    `Olá ${candidateName || "candidato(a)"}! 👋\n\nSua entrevista${
      jobTitle ? ` para a vaga de ${jobTitle}` : ""
    } foi agendada para ${startsAtLabel}.\n\n` +
    `Acesse a sala de vídeo por este link: ${candidateLink}\n\n` +
    `Nos vemos lá! 🎥`;

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} copiado!`))
      .catch(() => toast.error("Não consegui copiar."));
  };

  const handleSendWhatsapp = async () => {
    if (!candidateId) {
      toast.error("ID do candidato não disponível.");
      return;
    }
    if (!candidatePhone) {
      toast.error("Candidato não possui telefone cadastrado.");
      return;
    }
    setSendingWa(true);
    try {
      await axios.post(`${API}/notifications/whatsapp`, {
        candidate_id: candidateId,
        message: waMessage,
      });
      toast.success("Link da entrevista enviado por WhatsApp!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errorMsg =
        Array.isArray(detail)
          ? detail.map((e) => e?.msg || JSON.stringify(e)).join("; ")
          : typeof detail === "string"
          ? detail
          : "Erro ao enviar WhatsApp";
      toast.error(errorMsg);
    } finally {
      setSendingWa(false);
    }
  };

  const canSendWa = !!(candidateId && candidatePhone && candidateLink);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="interview-success-dialog"
        className="max-w-[520px] max-h-[90vh] overflow-y-auto overflow-x-hidden"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5 text-emerald-600" />
            Entrevista agendada
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 min-w-0">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div className="text-sm leading-snug">
              <strong className="text-emerald-900">Tudo certo!</strong>
              <p className="text-emerald-800 mt-0.5">
                {startsAtLabel} · {candidateName || "candidato"}
              </p>
            </div>
          </div>

          {room ? (
            <>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                  Link do gestor (você)
                </div>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 min-w-0">
                  <LinkIcon className="w-4 h-4 text-slate-500 shrink-0" />
                  <span
                    data-testid="host-join-url"
                    className="text-xs text-slate-700 font-mono truncate flex-1 min-w-0"
                  >
                    {hostLink}
                  </span>
                  <button
                    data-testid="copy-host-link"
                    type="button"
                    onClick={() => copy(hostLink, "Link do gestor")}
                    className="text-xs text-brand-blue hover:underline shrink-0 inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copiar
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                  Link do candidato (para enviar)
                </div>
                <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-md px-3 py-2 min-w-0">
                  <LinkIcon className="w-4 h-4 text-violet-600 shrink-0" />
                  <span
                    data-testid="candidate-join-url"
                    className="text-xs text-violet-900 font-mono truncate flex-1 min-w-0"
                  >
                    {candidateLink}
                  </span>
                  <button
                    data-testid="copy-candidate-link"
                    type="button"
                    onClick={() => copy(candidateLink, "Link do candidato")}
                    className="text-xs text-violet-700 hover:underline shrink-0 inline-flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copiar
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  💡 A sala já está pronta, mas você só precisa acessar no dia.
                  O candidato usa este link para entrar diretamente.
                </p>
              </div>

              {canSendWa && (
                <button
                  type="button"
                  data-testid="send-via-whatsapp"
                  onClick={handleSendWhatsapp}
                  disabled={sendingWa}
                  className="flex items-center justify-center gap-1.5 w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold py-2.5 px-3 rounded-md transition-colors text-sm"
                >
                  {sendingWa ? (
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 shrink-0" />
                  )}
                  <span className="truncate">
                    {sendingWa ? "Enviando…" : "Enviar link por WhatsApp"}
                  </span>
                </button>
              )}
            </>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
              A entrevista foi criada, mas a sala de vídeo ainda não está pronta.
              Você pode tentar abri-la pelo botão <strong>Vídeo</strong> na lista
              de entrevistas mais tarde.
            </div>
          )}

          {onCompleteInfo && (
            <button
              type="button"
              data-testid="open-additional-info"
              onClick={onCompleteInfo}
              className="flex items-center justify-center gap-1.5 w-full bg-white hover:bg-slate-50 border border-brand-blue text-brand-blue font-semibold py-2.5 px-3 rounded-md transition-colors text-sm"
            >
              <ClipboardList className="w-4 h-4 shrink-0" />
              Completar dados do candidato
            </button>
          )}

          <div className="flex justify-end pt-2 border-t border-slate-100">
            <Button
              data-testid="close-interview-success"
              onClick={onClose}
              variant="outline"
              size="sm"
            >
              Fechar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewSuccessDialog;
